/**
 * The handbook's client half.
 *
 * `KB_CATEGORIES` mirrors the server list (apps/api/src/modules/crm-kb/lib/
 * categories.ts) for the same reason the vault's kinds are mirrored: the labels
 * are i18n keys built off these strings, and a rail cannot wait for a request to
 * know its own shelves. It IS a mirror, so a shelf added on the server and not
 * here is a shelf whose guides land in no group.
 */
import { api } from "./api";

export const KB_CATEGORIES = ["process", "web", "design", "content", "seo", "care"] as const;
export type KbCategory = (typeof KB_CATEGORIES)[number];

export type KbStatus = "published" | "draft";

/** A picture (or a screen recording) attached to one slot. */
export interface KbShot {
  url: string;
  kind: "image" | "video";
  caption?: string;
  mediaId?: string;
}

/** What the index draws. Never carries a body. */
export interface KbCard {
  id: string;
  slug: string;
  category: string;
  title: string;
  summary: string;
  tags: string[];
  audience: string;
  order: number;
  status: KbStatus;
  readMinutes: number;
  /** Declared picture slots nobody has filled yet. */
  openShots: number;
  views: number;
  updatedAt: string;
}

export interface KbArticle extends KbCard {
  body: string;
  shots: Record<string, KbShot>;
  headings: { id: string; text: string }[];
  /** Born in the repo rather than written here. */
  seeded: boolean;
  /** The repo's master text has moved past what is shown. */
  sourceStale: boolean;
  editedAt: string | null;
  editedByName: string | null;
  version: number;
  /** A flow map is drawn for this guide. The page holds its space before it loads. */
  hasFlowMap: boolean;
}

export interface KbIndex {
  articles: KbCard[];
  counts: Record<string, number>;
  query: string | null;
}

export interface KbRevision {
  id: string;
  version: number;
  title: string;
  body: string;
  savedByName: string | null;
  note: string | null;
  at: string;
}

/** The fields an editor may change. */
export interface KbDraft {
  slug: string;
  category: KbCategory;
  title: string;
  summary: string;
  audience: string;
  tags: string[];
  order: number;
  status: KbStatus;
  body: string;
  note?: string;
}

export function listGuides(params: { q?: string; category?: string; includeDrafts?: boolean } = {}): Promise<KbIndex> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.category) qs.set("category", params.category);
  if (params.includeDrafts) qs.set("includeDrafts", "1");
  const tail = qs.toString();
  return api.get<KbIndex>(`/admin/crm/kb${tail ? `?${tail}` : ""}`);
}

export function getGuide(slug: string): Promise<KbArticle> {
  return api.get<KbArticle>(`/admin/crm/kb/${encodeURIComponent(slug)}`);
}

export function createGuide(draft: KbDraft): Promise<KbArticle> {
  return api.post<KbArticle>("/admin/crm/kb", draft);
}

export function updateGuide(id: string, patch: Partial<KbDraft>): Promise<KbArticle> {
  return api.patch<KbArticle>(`/admin/crm/kb/${id}`, patch);
}

export function deleteGuide(id: string): Promise<void> {
  return api.del<void>(`/admin/crm/kb/${id}`);
}

export function setGuideShot(id: string, key: string, shot: KbShot): Promise<KbArticle> {
  return api.put<KbArticle>(`/admin/crm/kb/${id}/shots/${encodeURIComponent(key)}`, shot);
}

export function clearGuideShot(id: string, key: string): Promise<KbArticle> {
  return api.del<KbArticle>(`/admin/crm/kb/${id}/shots/${encodeURIComponent(key)}`);
}

export function listGuideRevisions(id: string): Promise<{ revisions: KbRevision[] }> {
  return api.get<{ revisions: KbRevision[] }>(`/admin/crm/kb/${id}/revisions`);
}

export function getGuideSource(id: string): Promise<{ title: string; summary: string; body: string; stale: boolean }> {
  return api.get(`/admin/crm/kb/${id}/source`);
}

export function adoptGuideSource(id: string): Promise<KbArticle> {
  return api.post<KbArticle>(`/admin/crm/kb/${id}/adopt-source`);
}
